import { AxiosInstance } from "axios";
import i18n from "../i18n";
import { toStdLanguages } from "../toStdLanguages";
import axiosInstance, { axiosTravityInstance } from "./axiosInstance";
import { getPf } from "../utils/conf";

/**
 * Represents the scope of an internationalization (i18n) entry.
 */

/**
 * Defines the structure for an i18n search request payload.
 * All fields are optional.
 */
export interface I18nSearchRequest {
  scope?: "GLOBAL" | "LOCAL";
  entityType?: string;
  entityId?: string | number;
  entityIds?: string[] | number[]; // For batch requests
  language?: string;
  tlKey?: string; // Translation key to filter by
}

/**
 * Represents a single i18n entry returned from the API.
 * This is a sample structure and should be adjusted to match the actual API response.
 */
export interface I18nEntry {
  //   id: string;
  //   scope?: "GLOBAL" | "LOCAL";
  //   entityType: string;
  //   entityId: string;
  language: string;
  tlKey: string;
  value: string;
}

/**
 * Service class for handling i18n-related API calls.
 */
export class I18nService {
  /**
   * Searches for i18n entries based on specified filters.
   *
   * Sample request for /i18n/search:
   * {
   *   "scope": "GLOBAL",
   *   "entityType": "Product",
   *   "entityId": "123",
   *   "language": "EN"
   * }
   * All fields are optional. You can send an empty object {} or omit the body for no filters.
   *
   * @param {I18nSearchRequest} filters - The filtering criteria for the search. Defaults to an empty object.
   * @returns {Promise<I18nEntry[]>} A promise that resolves to an array of matching i18n entries.
   */
  public static async search(
    filters: I18nSearchRequest = {},
  ): Promise<I18nEntry[]> {
    try {
      const fetcher = async (instance: AxiosInstance) => {
        const response = await instance.post<ApiResponse<string[][]>>(
          "/i18n/search",
          {
            ...filters,
            language: filters.language
              ? toStdLanguages(filters.language)
              : undefined, // Ensure language is in standard format
          },
        );

        return response.data.result.map((entry) => {
          const [language, tlKey, value] = entry;
          return { language, tlKey, value } as I18nEntry;
        });
      };
      const ls = await Promise.all(
        (getPf() === "TRAVITY"
          ? [axiosInstance, axiosTravityInstance]
          : [axiosInstance]
        ).map(fetcher),
      );
      return ls.flat();
    } catch (error) {
      console.error("Error in I18nService.search:", error);
      // Re-throwing the error allows the calling component to handle it,
      // e.g., by showing a notification to the user.
      throw error;
    }
  }

  /**
   * Fetches remote i18n data and transforms it into a format suitable for i18next.
   *
   * @param {I18nSearchRequest} filters - The filtering criteria for the search
   * @returns {Promise<Record<string, Record<string, any>>>} A promise that resolves to i18next resource format
   */
  public static async fetchRemoteTranslations(
    filters: I18nSearchRequest = {},
  ): Promise<Record<string, Record<string, any>>> {
    try {
      if (Object.keys(filters).length === 0) {
        console.warn("No filters provided for remote loading, skipping.");
        return {};
      }
      const entries = await this.search(filters);
      // Transform the flat array into nested structure for i18next
      const resources: Record<string, Record<string, any>> = {};

      entries.forEach((entry) => {
        if (!entry.language || !entry.tlKey || !entry.value) {
          // console.warn("Invalid i18n entry:", entry);
          return; // Skip invalid entries
        }
        const { tlKey, value } = entry;
        const language = entry.language.toLowerCase();
        if (!resources[language]) {
          resources[language] = { translation: {} };
        }

        // Handle nested keys (e.g., "header.home")
        this.setNestedValue(resources[language].translation, tlKey, value);
      });
      return resources;
    } catch (error) {
      console.error("Error in I18nService.fetchRemoteTranslations:", error);
      throw error;
    }
  }

  /**
   * Loads remote translations and adds them to the existing i18next resources.
   *
   * @param {I18nSearchRequest} filters - The filtering criteria for the search
   * @param {boolean} override - Whether to override existing translations. Defaults to false.
   */
  public static async loadRemoteTranslations(
    filters: I18nSearchRequest = {},
    override: boolean = false,
  ): Promise<void> {
    try {
      const remoteResources = await this.fetchRemoteTranslations({
        ...filters,
      });

      // Add remote resources to i18next
      Object.keys(remoteResources).forEach((language) => {
        const existingResources =
          i18n.getResourceBundle(language, "translation") || {};
        const newResources = remoteResources[language].translation;

        const mergedResources = override
          ? { ...existingResources, ...newResources }
          : { ...newResources, ...existingResources };
        i18n.addResourceBundle(
          language,
          "translation",
          mergedResources,
          true,
          true,
        );
      });
    } catch (error) {
      console.error("Error in I18nService.loadRemoteTranslations:", error);
      throw error;
    }
  }

  /**
   * Fetches translations for a specific entity and language.
   *
   * @param {string} entityType - The type of entity (e.g., "Product", "Category")
   * @param {string} entityId - The ID of the entity
   * @param {string} language - The language code
   * @returns {Promise<Record<string, string>>} A promise that resolves to key-value translation pairs
   */
  public static async getEntityTranslations(
    entityType: string,
    entityId: string,
    language: string,
  ): Promise<Record<string, string>> {
    try {
      const entries = await this.search({
        entityType,
        entityId,
        language,
      });

      const translations: Record<string, string> = {};
      entries.forEach((entry) => {
        translations[entry.tlKey] = entry.value;
      });

      return translations;
    } catch (error) {
      console.error("Error in I18nService.getEntityTranslations:", error);
      throw error;
    }
  }

  /**
   * Refreshes i18next resources with the latest remote data.
   *
   * @param {I18nSearchRequest} filters - The filtering criteria for the search
   */
  public static async refreshTranslations(
    filters: I18nSearchRequest = {},
  ): Promise<void> {
    try {
      await this.loadRemoteTranslations(filters, true);
      // Trigger a re-render of components using translations
      await i18n.reloadResources();
    } catch (error) {
      console.error("Error in I18nService.refreshTranslations:", error);
      throw error;
    }
  }

  /**
   * Utility method to set nested values in an object using dot notation.
   *
   * @param {Record<string, any>} obj - The target object
   * @param {string} path - The dot-separated path (e.g., "header.home")
   * @param {any} value - The value to set
   */
  private static setNestedValue(
    obj: Record<string, any>,
    path: string,
    value: any,
  ): void {
    const keys = (path ?? "").split(".");
    let current = obj;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (!(key in current) || typeof current[key] !== "object") {
        current[key] = {};
      }
      current = current[key];
    }

    current[keys[keys.length - 1]] = value;
  }
}
